/**
 * URL de l'API backend — ordre de priorité :
 * 1. REACT_APP_BACKEND_URL (build / Netlify)
 * 2. window.__CHANTILLY_CONFIG__.BACKEND_URL (public/runtime-config.js, sans rebuild)
 * 3. Inférence depuis le domaine (chantillyclub.eu → api.lechantilly.fr)
 * 4. localhost:8001 en dev
 */

const DEFAULT_PRODUCTION_API = 'https://api.lechantilly.fr';
const DEFAULT_LOCAL_API = 'http://localhost:8001';

function normalizeUrl(url) {
  if (!url || url === 'undefined') return null;
  return String(url).replace(/\/$/, '');
}

function readRuntimeConfig() {
  if (typeof window !== 'undefined' && window.__CHANTILLY_CONFIG__?.BACKEND_URL) {
    return normalizeUrl(window.__CHANTILLY_CONFIG__.BACKEND_URL);
  }
  return null;
}

function inferFromHostname() {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return DEFAULT_LOCAL_API;
  if (
    host.includes('chantillyclub.eu')
    || host.includes('lechantilly.fr')
    || host.includes('chantilly-club.eu')
  ) {
    return DEFAULT_PRODUCTION_API;
  }
  // Emergent preview : FastAPI sur le même domaine que le frontend
  if (host.includes('emergentagent.com')) return window.location.origin;
  return null;
}

export function getBackendUrl() {
  return (
    normalizeUrl(process.env.REACT_APP_BACKEND_URL)
    || readRuntimeConfig()
    || inferFromHostname()
    || DEFAULT_LOCAL_API
  );
}

/** Extrait un message d'erreur depuis une réponse JSON FastAPI ou texte brut. */
export function apiErrorMessage(data, fallback = 'Erreur serveur') {
  if (!data) return fallback;
  if (typeof data === 'string') return data.slice(0, 300) || fallback;
  if (data.detail) {
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) {
      return data.detail.map((d) => d.msg || d.message || String(d)).join(', ');
    }
  }
  if (data.message) return data.message;
  return fallback;
}

/** Parse une réponse API ; message clair si le serveur renvoie du HTML ou du texte brut. */
export async function parseApiResponse(response, requestUrl = '') {
  const text = await response.text();
  const trimmed = text.trim();
  const contentType = response.headers.get('content-type') || '';

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.parse(trimmed);
    } catch {
      /* corps JSON invalide — traiter comme texte ci-dessous */
    }
  }

  const isHtml = /^\s*</.test(trimmed);
  if (isHtml) {
    if (response.status === 502 || response.status === 503) {
      throw new Error(
        'Le serveur API est hors ligne (erreur 502). Sur Emergent : redémarrez le backend Python, puis vérifiez /api/health dans le navigateur.'
      );
    }
    const hint = requestUrl ? ` (${requestUrl})` : '';
    throw new Error(
      `Impossible de joindre l'API${hint}. Vérifiez que le backend FastAPI tourne et que l'URL est correcte.`
    );
  }

  if (!response.ok || !contentType.includes('application/json')) {
    const msg = trimmed.slice(0, 300) || `Erreur serveur (${response.status})`;
    if (response.status === 500 && /^Internal Server Error/i.test(trimmed)) {
      const hint = requestUrl ? ` — ${requestUrl}` : '';
      throw new Error(
        `Le serveur a renvoyé une erreur interne${hint}. Vérifiez que l'URL API est correcte (runtime-config.js) et les logs backend.`
      );
    }
    throw new Error(msg);
  }

  return JSON.parse(trimmed);
}

export function authHeaders(extra = {}) {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/**
 * fetch avec timeout — évite le spinner infini quand le backend Emergent ne répond plus.
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {number} [timeoutMs=25000]
 */
export async function fetchWithTimeout(url, options = {}, timeoutMs = 25000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(
        'Le serveur API ne répond pas (timeout). Sur Emergent : redémarrez le backend Python, '
        + 'ouvrez /api/health dans le navigateur, puis réessayez le paiement.'
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
